// Main entry point for WordPress - handles auto-mounting
import '@uconnect/assets/frontend-globals.css';
import { createRoot } from 'react-dom/client';
import AgentUI from './AgentUI';
import AgentSidebar from './AgentSidebar';
import type { AgentUIConfig, PersonalizationSummary } from './types';

// Declare global config
declare global {
	interface Window {
		agentConfig: {
			agentHost: string;
			agentRoute?: string;
			siteId: number;
			jwtToken: string;
			agentWPApiBase: string;
			tokenPath?: string;
			isLoggedIn: boolean;
			isABTester: boolean;
			maxGuestSearches?: number;
			userId: number;
			signInUrl: string;
			ucRefParam?: string;
			initialPrompts?: string[];
			searchPosition?: string;
			fabStyle?: string | null;
			fabEffect?: string | null;
			fabIcon?: string | null;
			defaultOpen?: boolean;
			lmiPageUrl?: string | null;
			firstName?: string | null;
			personalization?: PersonalizationSummary | null;
			contentPreferencesUrl?: string;
		};
	}
}

/**
 * Auto-mount AgentUI to DOM elements on page load
 * This runs automatically when the script is loaded by WordPress
 */
document.addEventListener( 'DOMContentLoaded', () => {
	const configFromWP = window.agentConfig || {};

	// Mount all agent UI instances
	const agentUIElements = document.querySelectorAll( '[data-agent-ui]' );
	agentUIElements.forEach( ( element ) => {
		const htmlElement = element as HTMLElement;
		const dataset = htmlElement?.dataset;

		if ( ! dataset ) {
			console.warn( 'AgentUI: Element missing dataset', element );
			return;
		}

		const context = ( dataset.agentUi as AgentUIConfig['context'] ) || 'default';

		// Build config based on type
		const config: AgentUIConfig = {
			agentHost: configFromWP.agentHost,
			agentRoute: configFromWP.agentRoute,
			siteId: configFromWP.siteId,
			userId: configFromWP.userId,
			isLoggedIn: configFromWP.isLoggedIn,
			isABTester: configFromWP.isABTester || false,
			maxGuestSearches: configFromWP.maxGuestSearches !== undefined ? Number( configFromWP.maxGuestSearches ) : undefined,
			signInUrl: configFromWP.signInUrl,
			title: dataset.title || undefined,
			context,
			className: dataset.className || '',
			searchPosition: ( configFromWP.searchPosition as 'left' | 'right' ) || 'right',
			initialPrompts: configFromWP.initialPrompts,
			fabStyle: configFromWP.fabStyle || null,
			fabEffect: configFromWP.fabEffect || null,
			fabIcon: configFromWP.fabIcon || null,
			firstName: configFromWP.firstName || null,
			personalization: configFromWP.personalization || null,
			contentPreferencesUrl: configFromWP.contentPreferencesUrl,
		};

		// Sidebar context uses special dialog-based component
		if ( context === 'studentSidebar' ) {
			createRoot( element ).render( <AgentSidebar config={ config } /> );
		} else {
			createRoot( element ).render( <AgentUI config={ config } /> );
		}
	} );
} );
